import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/api";
import { BRAND, BRAND_SUB, DEPT_LINE1, DEPT_LINE2 } from "@/lib/constants";
import { HandHeart, LogIn, ShieldCheck, Users, Sparkles, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

const portals = [
  { key: "admin", label: "المدير العام", desc: "المتابعة والإحصائيات المركزية", icon: ShieldCheck },
  { key: "monitor", label: "فريق المتابعة", desc: "متابعة مركزية بصلاحيات الإدارة", icon: ClipboardCheck },
  { key: "coordinator", label: "منسق التطوع", desc: "إدارة أنشطة وبيانات المدرسة", icon: Users },
];

const LOGIN_PHRASES = [
  "أنت قائدُ التطوّع في مدرستك… انطلق بأثرك اليوم 🌟",
  "خطوةٌ منك اليوم، تصنع جيلاً معطاءً غداً 🌱",
  "بصمتك في الخير تبقى وإن غاب اسمك… فاجعلها جميلة ✨",
  "كل بيانٍ تُوثّقه هو أثرٌ خالد وسِجلُّ خيرٍ لا يُمحى 🤍",
  "توكّل على الله وابدأ… فالتوفيق يسبق المُخلصين دائماً 🤲",
  "أنت شمعةٌ تُضيء درب مدرستك ومجتمعك… فأنِر 🕊️",
  "قِف بثقة، فأنت تبني وطناً بالعطاء والإنجاز 🏅",
  "همّتك اليوم تصنع فارقاً في حياة طالبٍ وأسرة ومجتمع 🤝",
];

export default function Login() {
  const { login, loginCoordinator, loginMonitor, setUser } = useAuth();
  const [portal, setPortal] = useState("admin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [phraseIdx, setPhraseIdx] = useState(() => Math.floor(Math.random() * LOGIN_PHRASES.length));

  useEffect(() => {
    const t = setInterval(() => setPhraseIdx((p) => (p + 1) % LOGIN_PHRASES.length), 6000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("login_remember") || "null");
      if (saved) {
        setPortal(saved.portal || "admin");
        setEmail(saved.email || "");
        setPassword(saved.password || "");
        setPhone(saved.phone || "");
        setRemember(true);
      }
    } catch (e) { /* ignore */ }
  }, []);

  const inputCls = "w-full px-4 py-3 rounded-xl bg-amber-50 border border-amber-400/30 text-stone-800 focus:border-sky-400 focus:outline-none transition";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (remember) {
      localStorage.setItem("login_remember", JSON.stringify({ portal, email: email.trim(), password, phone: phone.trim() }));
    } else {
      localStorage.removeItem("login_remember");
    }
    try {
      if (portal === "coordinator") {
        const cu = await loginCoordinator(email.trim(), phone.trim());
        toast.success(cu?.name ? `أهلاً بك، ${cu.name}` : "مرحباً بك");
      } else if (portal === "monitor") {
        const mu = await loginMonitor(email.trim(), phone.trim());
        toast.success(mu?.name ? `أهلاً بك، ${mu.name} 🤍` : "مرحباً بك في فريق المتابعة");
      } else {
        const u = await login(email, password);
        if (u.role !== "admin") {
          localStorage.removeItem("token");
          setUser(null);
          toast.error("هذا الحساب ليس حساب مدير عام. اختر بوابة منسق التطوع.");
          return;
        }
        toast.success("مرحباً بك");
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" dir="rtl">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full bg-sky-400/15 blur-3xl" />
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 rounded-full bg-amber-500/12 blur-3xl" />
      </div>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md glass rounded-3xl p-8 sky-glow"
      >
        <div className="flex flex-col items-center text-center mb-6">
          <div className="w-16 h-16 rounded-3xl icon-sky flex items-center justify-center mb-4 animate-floaty">
            <HandHeart size={32} />
          </div>
          <h1 className="text-3xl font-head font-bold gold-text">{BRAND}</h1>
          <p className="text-amber-700 mt-2 text-sm font-head leading-relaxed">{BRAND_SUB}</p>
          <p className="text-stone-500 mt-2 text-xs">بصمتك في الخير تبقى</p>
        </div>

        <div
          className="mb-6 rounded-2xl px-4 py-3 flex items-center gap-3 border border-sky-200/60 bg-gradient-to-l from-sky-50/90 to-amber-50/70 overflow-hidden min-h-[64px]"
          data-testid="login-inspire-banner"
        >
          <div className="w-9 h-9 rounded-xl icon-sky flex items-center justify-center shrink-0 animate-floaty">
            <Sparkles size={18} />
          </div>
          <AnimatePresence mode="wait">
            <motion.p
              key={phraseIdx}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.5 }}
              className="text-sm font-medium text-stone-700 leading-relaxed"
            >
              {LOGIN_PHRASES[phraseIdx]}
            </motion.p>
          </AnimatePresence>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-6">
          {portals.map((p) => {
            const active = portal === p.key;
            return (
              <button
                key={p.key}
                type="button"
                data-testid={`portal-${p.key}`}
                onClick={() => setPortal(p.key)}
                className={`rounded-2xl p-2.5 border text-center transition-all duration-200 ${
                  active
                    ? "bg-gradient-to-b from-sky-500/20 to-sky-400/5 border-sky-400/60 sky-glow"
                    : "bg-white/50 border-stone-300/50 hover:border-sky-300"
                }`}
              >
                <p.icon size={22} className={`mx-auto mb-1 ${active ? "text-sky-600" : "text-stone-400"}`} />
                <div className={`text-[13px] font-bold leading-tight ${active ? "text-sky-900" : "text-stone-600"}`}>{p.label}</div>
                <div className="text-[9px] text-stone-500 mt-0.5 leading-tight">{p.desc}</div>
              </button>
            );
          })}
        </div>

        <form onSubmit={submit} className="space-y-4">
          {portal === "coordinator" || portal === "monitor" ? (
            <>
              <div>
                <label className="text-sm text-stone-500 mb-1.5 block">البريد الإلكتروني</label>
                <input data-testid="login-coord-email-input" name="email" autoComplete="username" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className={inputCls} placeholder="example@edu.sa" />
              </div>
              <div>
                <label className="text-sm text-stone-500 mb-1.5 block">رقم الجوال</label>
                <input data-testid="login-phone-input" name="phone" autoComplete="tel" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required className={inputCls} placeholder="05xxxxxxxx" />
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="text-sm text-stone-500 mb-1.5 block">البريد الإلكتروني</label>
                <input data-testid="login-email-input" name="email" autoComplete="username" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className={inputCls} placeholder="example@edu.sa" />
              </div>
              <div>
                <label className="text-sm text-stone-500 mb-1.5 block">كلمة المرور <span className="text-xs text-stone-400">(المشرفون: رقم الجوال)</span></label>
                <input data-testid="login-password-input" name="password" autoComplete="current-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className={inputCls} placeholder="••••••••" />
              </div>
            </>
          )}
          <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="remember-me-label">
            <input type="checkbox" data-testid="remember-me-checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="w-4 h-4 accent-sky-500" />
            <span className="text-sm text-stone-600">تذكّرني واحفظ بياناتي على هذا الجهاز</span>
          </label>
          <button
            data-testid="login-submit-btn"
            type="submit"
            disabled={loading}
            className="btn-gold w-full py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? "جارٍ الدخول..." : <><LogIn size={20} /> {portal === "admin" ? "دخول بوابة المدير العام" : portal === "monitor" ? "دخول فريق المتابعة" : "دخول بوابة المنسق"}</>}
          </button>
        </form>

        <div className="mt-7 pt-5 border-t border-amber-400/15 text-center">
          <p className="text-xs text-stone-500">{DEPT_LINE1}</p>
          <p className="text-xs text-stone-500">{DEPT_LINE2}</p>
        </div>
      </motion.div>
    </div>
  );
}
